import { useEffect, useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listProjectsApi, listProjectLogsApi, listUsersApi } from '../api/http';
import { FaBookOpen, FaRegClock, FaCalendarAlt, FaProjectDiagram, FaMoneyBill, FaUser } from 'react-icons/fa';

/* ================= Helpers ================= */
const pad = (n) => String(n).padStart(2, '0');

function parseSafeDateAny(v) {
  if (!v) return null;
  const t = typeof v === 'string' ? v.replace(' ', 'T') : v;
  const d = new Date(t);
  return isNaN(d) ? null : d;
}

function getLogDate(log) {
  if (log?.date) {
    const dateStr = String(log.date);
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const d = parseSafeDateAny(dateStr);
      if (d) return d;
    }
  }
  return parseSafeDateAny(log?.started_at || log?.startedAt || log?.created_at || log?.createdAt);
}

function formatCL(dt) {
  if (!dt) return '';
  return `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()}`;
}

function getUserName(log) {
  return log?.user_name || log?.member_name || log?.username || log?.user?.name || log?.user || '—';
}

function getHours(log) {
  if (log?.hours != null) return Number(log.hours) || 0;

  const toMinutes = (hhmm) => {
    if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
    const [h, m] = hhmm.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  };

  const ms = toMinutes(log?.start || log?.start_time);
  const me = toMinutes(log?.end || log?.end_time);
  if (ms != null && me != null && me > ms) {
    return Math.round(((me - ms) / 60) * 100) / 100;
  }

  const sa = parseSafeDateAny(log?.started_at || log?.startedAt);
  const ea = parseSafeDateAny(log?.ended_at || log?.endedAt);
  if (sa && ea && ea > sa) {
    return Math.round((((ea - sa) / 1000 / 60 / 60)) * 100) / 100;
  }

  return 0;
}

function htmlToPlain(textOrHtml = '') {
  const div = document.createElement('div');
  div.innerHTML = String(textOrHtml ?? '');
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
}

function getAmount(log) {
  if (log?.amount != null) return Number(log.amount) || 0;
  const h = getHours(log);
  const rate = Number(log?.rate ?? log?.hourly_rate_snapshot ?? log?.hourly_rate ?? 0) || 0;
  return Math.round(h * rate);
}

const fmtCLP = (n) => Number(n || 0).toLocaleString('es-CL', {
  style: 'currency',
  currency: 'CLP',
  maximumFractionDigits: 0
});

function getFilteredLogsByPeriod(logs, period) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  return logs.filter(log => {
    const logDate = getLogDate(log);
    if (!logDate) return false;

    switch (period) {
      case 'day':
        return logDate >= today && logDate < tomorrow;
      case 'month':
        return logDate >= thisMonth;
      case 'all':
      default:
        return true;
    }
  });
}

function getPeriodLabel(period) {
  switch (period) {
    case 'day': return 'Hoy';
    case 'month': return 'Este Mes';
    case 'all': return 'Total';
    default: return '';
  }
}

export default function Home() {
  const { user, hasRole, token } = useAuth();
  const canSeeUsers = hasRole ? hasRole('admin') : (user?.role === 'admin');
  const isAdmin = hasRole ? hasRole('admin') : (user?.role === 'admin');
  const myId = user?.id != null ? String(user.id) : '';

  const [projects, setProjects] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myProjectIds, setMyProjectIds] = useState(new Set());

  const [amountPeriod, setAmountPeriod] = useState('month');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const myPs = isAdmin
          ? await listProjectsApi(token)
          : await listProjectsApi(token, { mine: true });

        const myProjectIdsSet = new Set((myPs || []).map(p => p.id));
        setMyProjectIds(myProjectIdsSet);

        const activeProjects = (myPs || []).filter(p =>
          p.status === 'active' || p.status === 'planned'
        );
        setMyProjects(activeProjects);

        const allPs = await listProjectsApi(token);
        setProjects(allPs || []);

        if (isAdmin) {
          const usersData = await listUsersApi(token);
          setUsers((usersData || []).filter(u => u.status === 'active'));
        }

        const logsPromises = (myPs || []).map(async (p) => {
          try {
            const limit = 999999;
            const logs = await listProjectLogsApi(p.id, token, limit);
            return (logs || []).map(log => ({
              ...log,
              project_id: p.id,
              project_name: p.name,
              project_status: p.status
            }));
          } catch {
            return [];
          }
        });

        const allLogs = await Promise.all(logsPromises);
        const flatLogs = allLogs.flat();

        const filteredLogs = isAdmin
          ? flatLogs
          : flatLogs.filter(log => {
            const logUserId = String(log?.user_id || log?.userId || log?.user?.id || '');
            return logUserId === myId;
          });

        const sorted = filteredLogs.sort((a, b) => {
          const dateA = getLogDate(a);
          const dateB = getLogDate(b);
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB - dateA;
        });

        setRecentLogs(sorted);
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadData();
    }
  }, [token, isAdmin, myId]);

  const stats = useMemo(() => {
    const filteredLogs = getFilteredLogsByPeriod(recentLogs, amountPeriod);

    const totalHours = filteredLogs.reduce((sum, log) => sum + getHours(log), 0);
    const projectsCount = new Set(filteredLogs.map(l => l.project_id)).size;
    const totalAmount = filteredLogs.reduce((sum, log) => sum + getAmount(log), 0);

    return {
      totalHours: Math.round(totalHours * 100) / 100,
      logsCount: filteredLogs.length,
      projectsCount,
      totalAmount
    };
  }, [recentLogs, amountPeriod]);

  const programmerStats = useMemo(() => {
    const filteredLogs = getFilteredLogsByPeriod(recentLogs, amountPeriod);
    const statsByUser = {};

    filteredLogs.forEach(log => {
      const userId = String(log?.user_id || log?.userId || log?.user?.id || '');
      const userName = getUserName(log);

      if (!userId) return;

      if (!isAdmin && userId !== myId) return;

      if (!statsByUser[userId]) {
        statsByUser[userId] = {
          userId,
          userName,
          hours: 0,
          amount: 0,
          logsCount: 0
        };
      }

      statsByUser[userId].hours += getHours(log);
      statsByUser[userId].amount += getAmount(log);
      statsByUser[userId].logsCount += 1;
    });

    return Object.values(statsByUser)
      .map(stat => ({
        ...stat,
        hours: Math.round(stat.hours * 100) / 100
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [recentLogs, amountPeriod, isAdmin, myId]);

  const todayLogs = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

    const filtered = recentLogs.filter(log => {
      const logDate = getLogDate(log);
      if (!logDate) return false;

      if (!isAdmin) {
        const logUserId = String(log?.user_id || log?.userId || log?.user?.id || '');
        if (logUserId !== myId) return false;
      }

      return logDate >= today && logDate < tomorrow;
    });

    return filtered.sort((a, b) => {
      const getFullTimestamp = (log) => {
        const timestamp = parseSafeDateAny(log?.started_at || log?.startedAt || log?.created_at || log?.createdAt);
        if (timestamp) return timestamp.getTime();
        
        const logDate = getLogDate(log);
        if (logDate) {
          const startTime = log?.start || log?.start_time;
          if (startTime && typeof startTime === 'string') {
            const [hours, minutes] = startTime.split(':').map(Number);
            if (!isNaN(hours) && !isNaN(minutes)) {
              const dateWithTime = new Date(logDate);
              dateWithTime.setHours(hours, minutes, 0, 0);
              return dateWithTime.getTime();
            }
          }
          return logDate.getTime();
        }
        
        return 0;
      };

      const timeA = getFullTimestamp(a);
      const timeB = getFullTimestamp(b);
      
      return timeB - timeA;
    });
  }, [recentLogs, isAdmin, myId]);

  function getStartEndTime(log) {
    const start = log?.start || log?.start_time;
    const end = log?.end || log?.end_time;

    if (start && end) {
      return `${start} - ${end}`;
    } else if (start) {
      return `${start}`;
    } else if (end) {
      return `hasta ${end}`;
    }
    return null;
  }

  return (
    <Layout title="Inicio">
      <div className="card">
        <h2 style={{ marginTop: 0 }}>¡Hola, {user?.name}!</h2>
        <p>Rol: <b>{user?.role}</b></p>

        {/* Acciones rápidas */}
        <div className="action-buttons">
          {canSeeUsers && (
            <Link className="btn btn-gradient-dark" to="/users">
              Mantenedor de Programadores
            </Link>
          )}
          <Link className="btn btn-gradient-blue" to="/projects">
            <FaProjectDiagram className="btn-icon" />
            Ver Proyectos
          </Link>
          <Link className="btn btn-gradient-purple" to="/bitacora">
            <FaBookOpen className="btn-icon" />
            Bitácora General
          </Link>
        </div>

        {/* Filtro global de período */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          padding: '1rem',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: '12px',
          border: '2px solid #e2e8f0'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#1e293b' }}>
            Estadísticas de {getPeriodLabel(amountPeriod)} {isAdmin && '(Todos los usuarios)'}
          </h3>
          <select
            value={amountPeriod}
            onChange={(e) => setAmountPeriod(e.target.value)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: '2px solid #e5e7eb',
              background: 'white',
              color: '#1f2937',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.2s ease'
            }}
          >
            <option value="day">Hoy</option>
            <option value="month">Este Mes</option>
            <option value="all">Todo</option>
          </select>
        </div>

        {/* Estadísticas rápidas */}
        <div className="stats-grid">
          <div className="stat-card stat-card-blue">
            <div className="stat-card-content">
              <div className="stat-card-info">
                <p className="stat-card-label">Horas Trabajadas</p>
                <p className="stat-card-value">{stats.totalHours}h</p>
              </div>
              <FaRegClock className="stat-card-icon" />
            </div>
          </div>

          <div className="stat-card stat-card-green">
            <div className="stat-card-content">
              <div className="stat-card-info">
                <p className="stat-card-label">Proyectos</p>
                <p className="stat-card-value">{stats.projectsCount}</p>
              </div>
              <FaProjectDiagram className="stat-card-icon" />
            </div>
          </div>

          <div className="stat-card stat-card-purple">
            <div className="stat-card-content">
              <div className="stat-card-info">
                <p className="stat-card-label">Registros</p>
                <p className="stat-card-value">{stats.logsCount}</p>
              </div>
              <FaCalendarAlt className="stat-card-icon" />
            </div>
          </div>

          <div className="stat-card stat-card-orange">
            <div className="stat-card-content">
              <div className="stat-card-info">
                <p className="stat-card-label">Ingresos</p>
                <p className="stat-card-value">
                  {fmtCLP(stats.totalAmount)}
                </p>
              </div>
              <FaMoneyBill className="stat-card-icon" />
            </div>
          </div>
        </div>

        {/* Estadísticas por Programador */}
        {programmerStats.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 className="section-title">
              {isAdmin ? 'Ingresos por Programador' : 'Mis Ingresos'}
            </h3>
            <div className="stats-grid">
              {programmerStats.map((stat) => (
                <div
                  key={stat.userId}
                  className="stat-card"
                  style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: '2px solid #e2e8f0'
                  }}
                >
                  <div className="stat-card-content">
                    <div className="stat-card-info">
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px'
                      }}>
                        <FaUser style={{ color: '#64748b', fontSize: '1rem' }} />
                        <p style={{
                          margin: 0,
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#1e293b'
                        }}>
                          {stat.userName}
                        </p>
                      </div>
                      <p className="stat-card-value" style={{
                        fontSize: '1.5rem',
                        color: '#64748b'
                      }}>
                        {fmtCLP(stat.amount)}
                      </p>
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        marginTop: '8px',
                        fontSize: '0.75rem',
                        color: '#64748b'
                      }}>
                        <span>
                          <FaRegClock style={{ marginRight: '4px' }} />
                          {stat.hours}h
                        </span>
                        <span>
                          <FaCalendarAlt style={{ marginRight: '4px' }} />
                          {stat.logsCount} registros
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mis Proyectos */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 className="section-title">{isAdmin ? 'Proyectos Activos' : 'Mis Proyectos'}</h3>
          {loading ? (
            <div className="loading-state">Cargando proyectos...</div>
          ) : myProjects.length > 0 ? (
            <div className="projects-grid">
              {myProjects.map(project => (
                <Link
                  key={project.id}
                  to={`/worklog/${project.id}`}
                  className="project-card"
                >
                  <div className="project-card-content">
                    <div className="project-card-info">
                      <h4 className="project-card-title">{project.name}</h4>
                      {project.client_name && (
                        <p className="project-card-client">Cliente: {project.client_name}</p>
                      )}
                    </div>
                    <span className={`project-status-badge ${project.status === 'active' ? 'project-status-active' :
                      project.status === 'planned' ? 'project-status-planned' :
                        project.status === 'on_hold' ? 'project-status-on_hold' :
                          'project-status-default'
                      }`}>
                      {project.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              {isAdmin ? 'No hay proyectos activos' : 'No tienes proyectos asignados actualmente'}
            </div>
          )}
        </div>

        <div>
          <div className="section-header">
            <h3 className="section-title">
              Registros de Hoy {isAdmin && '(Todos los usuarios)'}
            </h3>
            <Link to="/bitacora" className="section-link">
              Ver todos →
            </Link>
          </div>

          {loading ? (
            <div className="loading-state">Cargando registros...</div>
          ) : todayLogs.length > 0 ? (
            <div className="worklog-list">
              {todayLogs.slice(0, 10).map((log, idx) => {
                const logDate = getLogDate(log);
                const hours = getHours(log);
                const noteText = htmlToPlain(log?.progress_note || log?.note || '');
                const hasAccess = myProjectIds.has(log.project_id);
                const logUserName = getUserName(log);
                const isMyLog = String(log?.user_id || log?.userId || log?.user?.id || '') === myId;
                const timeRange = getStartEndTime(log);

                return (
                  <div key={log.id || idx} className="worklog-item">
                    <div className="worklog-header">
                      <div className="worklog-project-info">
                        {hasAccess ? (
                          <Link
                            to={`/worklog/${log.project_id}`}
                            className="worklog-project-link"
                          >
                            {log.project_name}
                          </Link>
                        ) : (
                          <span className="worklog-project-link" style={{ cursor: 'default', opacity: 0.6 }}>
                            {log.project_name}
                          </span>
                        )}
                        {isAdmin && (
                          <span className="worklog-user" style={{
                            fontWeight: !isMyLog ? 600 : 400,
                            color: !isMyLog ? '#3b82f6' : 'inherit'
                          }}>
                            • {logUserName}
                          </span>
                        )}
                      </div>
                      <div className="worklog-hours">
                        <FaRegClock className="worklog-hours-icon" />
                        <span className="worklog-hours-value">{hours}h</span>
                      </div>
                    </div>

                    <div className="worklog-meta">
                      <div className="worklog-date">
                        <FaCalendarAlt className="worklog-date-icon" />
                        <span>{logDate ? formatCL(logDate) : 'Sin fecha'}</span>
                      </div>
                      {timeRange && (
                        <div className="worklog-time-range" style={{
                          fontSize: '0.875rem',
                          color: '#64748b',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <FaRegClock style={{ fontSize: '0.75rem' }} />
                          <span>{timeRange}</span>
                        </div>
                      )}
                      {log.activity_code && (
                        <span className="worklog-activity-badge">
                          {log.activity_code}
                        </span>
                      )}
                    </div>

                    {noteText && (
                      <p className="worklog-note">
                        {noteText.length > 150 ? noteText.substring(0, 150) + '...' : noteText}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <FaBookOpen className="empty-state-icon" />
              <p className="empty-state-text">No hay registros de hoy</p>
              <Link to="/projects" className="empty-state-link">
                Registrar horas →
              </Link>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}